import { getLocale, t } from "@travily/i18n";
import { formatPriceWhole } from "@ui/lib/format-price";
import { tv } from "@ui/lib/tv";
import { PUBLISHED_CURRENCY } from "../../apply-household";

const seasonalityStrip = tv({
  slots: {
    root: "flex flex-col gap-2",
    title: "font-medium text-neutral-500 text-xs uppercase tracking-wider",
    chart: "flex h-20 items-end gap-1",
    column: "flex flex-1 flex-col items-center gap-1",
    bar: "w-full rounded-t bg-red-200",
    month: "text-[10px] text-neutral-400",
  },
});

export interface SeasonalityStripProps {
  /** The household total for each month of the year, January first. */
  monthly: number[];
}

const MONTHS = Array.from({ length: 12 }, (_, month) => month);

// 2026 is arbitrary and never shown — only the month's initial is formatted.
function monthInitial(month: number): string {
  return new Intl.DateTimeFormat(getLocale(), { month: "narrow" }).format(new Date(2026, month, 1));
}

/**
 * The year in twelve plain bars. Scaled against the dearest month rather than
 * zero: the interesting thing about a place's seasonality is the spread across
 * it, and a zero-based axis flattens a 20% swing into nothing.
 */
export function SeasonalityStrip({ monthly }: SeasonalityStripProps) {
  const styles = seasonalityStrip();
  const peak = Math.max(...monthly);
  const floor = Math.min(...monthly);
  const span = peak - floor || peak || 1;

  return (
    <div className={styles.root()}>
      <h3 className={styles.title()}>{t("components.views.destinations.costs.seasonality.title")}</h3>
      <div className={styles.chart()}>
        {MONTHS.map((month) => {
          const value = monthly[month] ?? 0;

          return (
            <div className={styles.column()} key={month}>
              <span
                className={styles.bar()}
                style={{ height: `${20 + ((value - floor) / span) * 80}%` }}
                title={formatPriceWhole(value, PUBLISHED_CURRENCY)}
              />
              <span className={styles.month()}>{monthInitial(month)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
